import { defineStore } from 'pinia'

import { getGeoParameters } from '@/api/call-flows-routes'

import { useDefaultStore } from '@/store/defaultStore'
import { useDetailCallFlowsStore } from '@/store/call-tracking/callFlow/detailCallFlowsStore'

import { parseParams } from '@/helpers/destinations/destination-request-helper'

import { CALL_FLOW_COMPONENTS } from '@/constants/call-tracking/call-flow/callFlowComponents'
import { ROUTE_CALLS } from '@/constants/call-tracking/call-flow/geoRoutingRouteCalls'
import { ZIP_CODE_FETCHING } from '@/constants/call-tracking/call-flow/geoRoutingFetchType'

export const useDetailCallFlowGeoParametersStore = defineStore('detailCallFlowGeoParameters', {
    state: () => ({
        loading: true,
        items: [],
        parseParameters: [],
    }),
    actions: {
        async goToLoadGeoParameters(slug) {
            const defaultStore = useDefaultStore()
            this.loading = true

            const { success, payload, message } = await getGeoParameters(slug)

            if (success) {
                this.items = payload
                this.parseParameters = parseParams(payload)

                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.loading = false

            return { success }
        },

        addOrUpdateCallerInput(block) {
            const { slug, config } = block

            const findIndex = this.items['Call Flow Parameters'].findIndex((item) => item.slug === slug)

            if (findIndex !== -1) {
                this.items['Call Flow Parameters'][findIndex] = { ...this.items['Call Flow Parameters'][findIndex], key: config.parameter_name, id: slug }
            } else {
                this.items['Call Flow Parameters'] = [...this.items['Call Flow Parameters'], { key: config.parameter_name, slug, id: slug }]
            }

            this.parseParameters = parseParams(this.items)
        },

        addOrUpdateHttpRouter(block) {
            const detailCallFlowsStore = useDetailCallFlowsStore()

            if (!this.items['HTTP Router']) {
                this.items['HTTP Router'] = []
            }

            this.rebuildAllHttpRouterParameters(detailCallFlowsStore.blocks)
            this.parseParameters = parseParams(this.items)
        },

        rebuildAllHttpRouterParameters(blocks) {
            const allHttpRouterParameters = []

            blocks.forEach((block) => {
                if (block.type_id === CALL_FLOW_COMPONENTS.httpRouter.id && block.config && block.config.response_parsing) {
                    const filteredItems = block.config.response_parsing
                        .filter((item) => !!item.key && !!item.value)

                    filteredItems.forEach((parameter) => {
                        allHttpRouterParameters.push({
                            id: parameter.id,
                            key: parameter.key,
                            slug: block.slug,
                        })
                    })
                }
            })

            this.items['HTTP Router'] = allHttpRouterParameters
        },

        deleteHttpRouterParameter(blockSlug, parameterId) {
            if (!this.items['HTTP Router']) {
                return
            }

            this.items['HTTP Router'] = this.items['HTTP Router'].filter(
                (item) => !(item.slug === blockSlug && item.id === parameterId),
            )

            this.parseParameters = parseParams(this.items)
        },

        deleteHttpRouterBlock(block) {
            if (!this.items['HTTP Router']) {
                return
            }

            this.items['HTTP Router'] = this.items['HTTP Router'].filter((item) => item.slug !== block.slug)

            this.parseParameters = parseParams(this.items)
        },

        deleteFilterBySlug(slug) {
            this.items['Call Flow Parameters'] = this.items['Call Flow Parameters'].filter((item) => item.id !== slug || item.slug !== slug)

            this.parseParameters = parseParams(this.items)
        },

        validateLocations(locations) {
            return !!locations.length && locations.every((p) => !!p.codes.length)
        },

        checkValidInput(block) {
            const voiceMailFormData = {
                audio_url: block.config.input.audio_url,
                description: block.config.input.description,
                language: block.config.input.description,
                voice: block.config.input.description,
            }

            return Object.values(voiceMailFormData).some((value) => value) && !!block.config.input.valid_digits && this.validateLocations(block.config.locations)
        },

        checkValidParameters(block) {
            const voiceMailFormData = {
                audio_url: block.config.params.audio_url,
                description: block.config.params.description,
                language: block.config.params.description,
                voice: block.config.params.description,
            }

            if (block.config.params.zip_code_prompt_enabled) {
                return Object.values(voiceMailFormData).some((value) => value) && !!block.config.input.valid_digits && this.validateLocations(block.config.locations) && block.config.params.parameters.length && block.config.params.parameters.every((p) => !!p.key)
            }

            return this.validateLocations(block.config.locations) && block.config.params.parameters.length && block.config.params.parameters.every((p) => !!p.key)
        },

        checkValidBlock(block) {
            const detailCallFlowsStore = useDetailCallFlowsStore()
            if (block.config.routing_type === ROUTE_CALLS.areaCode.id) {
                detailCallFlowsStore.goToUpdateBlockBySlug(block.slug, { ...block.config, valid: this.validateLocations(block.config.locations) })
                return
            }

            if (block.config.fetching_type === ZIP_CODE_FETCHING.input.id) {
                detailCallFlowsStore.goToUpdateBlockBySlug(block.slug, { ...block.config, valid: this.checkValidInput(block) })
                return
            }

            if (block.config.fetching_type === ZIP_CODE_FETCHING.parameters.id) {
                detailCallFlowsStore.goToUpdateBlockBySlug(block.slug, { ...block.config, valid: this.checkValidParameters(block) })
            }
        },
    },
})
